// UploadContentModal.js
// Upload and share study content with AI verification and file support

import React, { useState, useRef } from 'react';
import { X, Upload, FileText, Sparkles, Check, AlertCircle, Loader, Award, Edit2, File } from 'lucide-react';
import { extractPdfText } from './pdfExtractor';
import { extractPptText } from './pptExtractor';

const UploadContentModal = ({ darkMode, currentUser, supabase, makeOpenAIRequest, onClose, onSuccess, editingContent = null }) => {
  const [step, setStep] = useState(editingContent ? 'form' : 'upload'); // upload, extracting, form, verifying, results
  const [contentType, setContentType] = useState(editingContent?.content_type || 'notes');
  const [title, setTitle] = useState(editingContent?.title || '');
  const [description, setDescription] = useState(editingContent?.description || '');
  const [contentText, setContentText] = useState(editingContent?.content_data?.text || '');
  const [subject, setSubject] = useState(editingContent?.subject || '');
  const [difficulty, setDifficulty] = useState(editingContent?.difficulty_level || 'intermediate');
  const [tags, setTags] = useState(editingContent?.tags?.join(', ') || '');
  const [estimatedTime, setEstimatedTime] = useState(editingContent?.estimated_time_minutes?.toString() || '');
  const [shareToDiscover, setShareToDiscover] = useState(!editingContent);
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedContent, setExtractedContent] = useState(null);
  const fileInputRef = useRef(null);

  const contentTypes = [
    { value: 'notes', label: 'Notes', icon: '📝', description: 'Study notes and explanations', accept: '.pdf,.txt,.md,.ppt,.pptx' },
    { value: 'summary', label: 'Summary', icon: '📄', description: 'Concise topic summaries', accept: '.pdf,.txt,.md,.ppt,.pptx' },
    { value: 'quiz', label: 'Quiz', icon: '❓', description: 'Practice questions and answers', accept: '.pdf,.txt,.md' },
    { value: 'flashcards', label: 'Flashcards', icon: '🗂️', description: 'Quick review cards', accept: '.pdf,.txt,.md' },
    { value: 'study_guide', label: 'Study Guide', icon: '📖', description: 'Comprehensive guides', accept: '.pdf,.txt,.md,.ppt,.pptx' },
    { value: 'video_link', label: 'Video Link', icon: '🎥', description: 'Educational video resources', accept: '' }
  ];

  const subjects = ['Math', 'Science', 'English', 'History', 'SAT', 'ACT', 'NEET', 'JEE', 'Physics', 'Chemistry', 'Biology', 'Other'];

  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploadedFile(file);
    setStep('extracting');
    setLoading(true);
    setError(null);

    try {
      let extractedText = '';
      const fileExtension = file.name.split('.').pop().toLowerCase();

      // Extract text based on file type
      if (fileExtension === 'pdf') {
        extractedText = await extractPdfText(file);
      } else if (fileExtension === 'ppt' || fileExtension === 'pptx') {
        extractedText = await extractPptText(file);
      } else if (fileExtension === 'txt' || fileExtension === 'md') {
        extractedText = await file.text();
      } else {
        throw new Error('Unsupported file type');
      }

      if (!extractedText || extractedText.trim().length < 50) {
        throw new Error('Could not extract enough text from the file. Please try a different file or enter content manually.');
      }

      setExtractedContent(extractedText);
      setContentText(extractedText);

      // Use AI to generate title, description, and metadata
      await generateMetadataFromContent(extractedText, file.name);

    } catch (err) {
      console.error('Error extracting file:', err);
      setError(err.message || 'Failed to extract content from file. Please try again or enter content manually.');
      setStep('upload');
      setLoading(false);
    }
  };

  const generateMetadataFromContent = async (content, filename) => {
    try {
      const messages = [
        {
          role: 'system',
          content: `You are an AI that analyzes educational content and generates metadata. Based on the content provided, generate:
1. A clear, concise title (5-10 words)
2. A brief description (1-2 sentences)
3. Subject area (Math, Science, English, History, SAT, ACT, NEET, JEE, Physics, Chemistry, Biology, or Other)
4. Difficulty level (beginner, intermediate, or advanced)
5. Suggested tags (3-5 relevant keywords)
6. Estimated study time in minutes

Return a JSON object:
{
  "title": "Clear title based on content",
  "description": "Brief description of what students will learn",
  "subject": "Subject area",
  "difficulty": "beginner|intermediate|advanced",
  "tags": ["tag1", "tag2", "tag3"],
  "estimatedTime": 15
}`
        },
        {
          role: 'user',
          content: `Analyze this educational content and generate metadata:

Filename: ${filename}

Content:
${content.substring(0, 3000)}... ${content.length > 3000 ? '[content truncated]' : ''}

Generate appropriate metadata for this content.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 800);
      const metadata = JSON.parse(response);

      // Set the generated metadata
      setTitle(metadata.title || '');
      setDescription(metadata.description || '');
      setSubject(metadata.subject || '');
      setDifficulty(metadata.difficulty || 'intermediate');
      setTags(metadata.tags?.join(', ') || '');
      setEstimatedTime(metadata.estimatedTime?.toString() || '');

      setStep('form');
      setLoading(false);
    } catch (err) {
      console.error('Error generating metadata:', err);
      // If AI fails, still proceed to form with basic metadata
      setTitle(filename.replace(/\.[^/.]+$/, ''));
      setStep('form');
      setLoading(false);
    }
  };

  const verifyContent = async () => {
    setStep('verifying');
    setLoading(true);
    setError(null);

    try {
      // AI Verification - Check accuracy, readability, and quality
      const messages = [
        {
          role: 'system',
          content: `You are an educational content quality verifier. Analyze the submitted study content for:
1. Factual accuracy (verify facts, formulas, concepts)
2. Readability and clarity
3. Educational value
4. Appropriate difficulty level
5. Completeness and organization

Return a JSON object:
{
  "score": 0.85,
  "isApproved": true,
  "feedback": "Detailed feedback for the creator",
  "strengths": ["Clear explanations", "Good examples"],
  "improvements": ["Add more practice problems", "Clarify section 3"],
  "suggestedTags": ["chemistry", "organic", "reactions"],
  "factCheck": {
    "hasErrors": false,
    "errors": []
  },
  "readabilityScore": 0.9,
  "educationalValue": 0.88
}`
        },
        {
          role: 'user',
          content: `Verify this ${contentType} content:

Title: ${title}
Subject: ${subject}
Difficulty: ${difficulty}
Description: ${description}

Content:
${contentText}

Provide verification results with specific feedback.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 1500);
      const verification = JSON.parse(response);

      setVerificationResult(verification);
      setStep('results');

      // If approved, save to database
      if (verification.isApproved && shareToDiscover) {
        if (editingContent) {
          await updateContent(verification);
        } else {
          await saveToDiscover(verification);
        }
      }
    } catch (err) {
      console.error('Error verifying content:', err);
      setError('Failed to verify content. Please try again.');
      setStep('form');
    }

    setLoading(false);
  };

  const saveToDiscover = async (verification) => {
    try {
      const tagsArray = tags.split(',').map(t => t.trim()).filter(t => t);
      const allTags = [...tagsArray, ...(verification.suggestedTags || [])];

      // Format content with AI for better readability
      let formattedContent = contentText;
      if (contentText && contentText.length > 100) {
        try {
          const formatMessages = [
            {
              role: 'system',
              content: 'You are a formatting assistant. Reformat the given text to make it more readable by adding proper spacing, line breaks, and structure. DO NOT change the content or meaning - only improve formatting. Preserve all numbers, bullet points, and section markers. Add blank lines between sections for better readability.'
            },
            {
              role: 'user',
              content: `Please reformat this text for better readability without changing any content:\n\n${contentText.substring(0, 4000)}`
            }
          ];

          const formattedResponse = await makeOpenAIRequest(formatMessages, 4000);
          formattedContent = formattedResponse || contentText;
        } catch (err) {
          console.error('Error formatting content:', err);
          // If formatting fails, use original content
          formattedContent = contentText;
        }
      }

      const { data, error: insertError } = await supabase
        .from('discover_content')
        .insert({
          user_id: currentUser.id,
          title: title,
          description: description,
          content_type: contentType,
          content_data: {
            text: formattedContent,
            metadata: {
              uploadedAt: new Date().toISOString(),
              originalFilename: uploadedFile?.name || null,
              creatorName: currentUser.user_metadata?.name || currentUser.email?.split('@')[0] || 'Anonymous',
              creatorEmail: currentUser.email
            }
          },
          tags: Array.from(new Set(allTags)),
          subject: subject.toLowerCase(),
          difficulty_level: difficulty,
          estimated_time_minutes: parseInt(estimatedTime) || null,
          ai_verified: verification.isApproved,
          ai_verification_score: verification.score,
          ai_verification_feedback: verification.feedback,
          verification_status: verification.isApproved ? 'approved' : 'needs_review',
          is_published: verification.isApproved,
          published_at: verification.isApproved ? new Date().toISOString() : null
        })
        .select()
        .single();

      if (insertError) throw insertError;

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving to discover:', err);
      setError('Content verified but failed to save. Please try again.');
    }
  };

  const updateContent = async (verification) => {
    try {
      const tagsArray = tags.split(',').map(t => t.trim()).filter(t => t);
      const allTags = [...tagsArray, ...(verification.suggestedTags || [])];

      const { error: updateError } = await supabase
        .from('discover_content')
        .update({
          title: title,
          description: description,
          content_type: contentType,
          content_data: {
            text: contentText,
            metadata: {
              updatedAt: new Date().toISOString(),
              originalFilename: uploadedFile?.name || editingContent?.content_data?.metadata?.originalFilename || null
            }
          },
          tags: Array.from(new Set(allTags)),
          subject: subject.toLowerCase(),
          difficulty_level: difficulty,
          estimated_time_minutes: parseInt(estimatedTime) || null,
          ai_verified: verification.isApproved,
          ai_verification_score: verification.score,
          ai_verification_feedback: verification.feedback,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingContent.id)
        .eq('user_id', currentUser.id);

      if (updateError) throw updateError;

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error updating content:', err);
      setError('Failed to update content. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !contentText || !subject) {
      setError('Please fill in all required fields');
      return;
    }

    await verifyContent();
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className={`w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              {editingContent ? <Edit2 className="w-5 h-5 text-white" /> : <Upload className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {editingContent ? 'Edit Your Content' : 'Share Your Knowledge'}
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {editingContent ? 'Update your content' : 'Upload files or enter content manually'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-100px)] overflow-y-auto">
          {step === 'upload' && !editingContent && (
            <div className="space-y-6">
              {/* Content Type Selection */}
              <div>
                <label className={`block text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  What would you like to share? *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {contentTypes.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setContentType(type.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        contentType === type.value
                          ? 'border-purple-500 bg-purple-500/10'
                          : darkMode
                            ? 'border-gray-700 hover:border-purple-500/50'
                            : 'border-gray-200 hover:border-purple-400'
                      }`}
                    >
                      <div className="text-2xl mb-2">{type.icon}</div>
                      <div className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {type.label}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {type.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* File Upload Option */}
              {contentType !== 'video_link' && (
                <div className={`p-6 rounded-2xl border-2 border-dashed transition-all ${
                  darkMode
                    ? 'bg-gray-800/50 border-gray-700 hover:border-purple-500'
                    : 'bg-purple-50 border-purple-300 hover:border-purple-500'
                }`}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={contentTypes.find(t => t.value === contentType)?.accept}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleFileUpload(file);
                    }}
                    className="hidden"
                  />
                  <div className="text-center">
                    <File className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Upload Your File
                    </h3>
                    <p className={`mb-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      AI will automatically extract content and generate title, description, and tags
                    </p>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:scale-105 transition-transform"
                    >
                      Choose File
                    </button>
                    <p className={`mt-3 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Supported: {contentTypes.find(t => t.value === contentType)?.accept || 'PDF, TXT, MD, PPT, PPTX'}
                    </p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>OR</span>
              </div>

              {/* Manual Entry Option */}
              <button
                type="button"
                onClick={() => setStep('form')}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  darkMode
                    ? 'border-gray-700 hover:border-purple-500 text-white'
                    : 'border-gray-300 hover:border-purple-500 text-gray-900'
                }`}
              >
                <FileText className="w-6 h-6 mx-auto mb-2" />
                <span className="font-semibold">Enter Content Manually</span>
              </button>

              {error && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/50 text-red-500 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}

          {step === 'extracting' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                AI is Processing Your File
              </h3>
              <p className={`text-center mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Extracting content and generating metadata...
              </p>
              <div className="space-y-3 w-full max-w-md">
                {['Extracting text from file', 'Analyzing content', 'Generating title & description', 'Creating tags and metadata'].map((step, idx) => (
                  <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Show file info if uploaded */}
              {uploadedFile && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-green-900/20 border border-green-500/50' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <div>
                      <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        File uploaded: {uploadedFile.name}
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        AI has generated metadata below. You can edit any field before submitting.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Content Type (only show if not uploaded) */}
              {!editingContent && !uploadedFile && (
                <div>
                  <label className={`block text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Content Type *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {contentTypes.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setContentType(type.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          contentType === type.value
                            ? 'border-purple-500 bg-purple-500/10'
                            : darkMode
                              ? 'border-gray-700 hover:border-purple-500/50'
                              : 'border-gray-200 hover:border-purple-400'
                        }`}
                      >
                        <div className="text-2xl mb-2">{type.icon}</div>
                        <div className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {type.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Title * {uploadedFile && <span className="text-purple-500">(AI Generated - Edit if needed)</span>}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Quick Summary: Organic Chemistry Reactions"
                  className={`w-full px-4 py-3 rounded-xl border ${
                    darkMode
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description * {uploadedFile && <span className="text-purple-500">(AI Generated - Edit if needed)</span>}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of what students will learn..."
                  rows={3}
                  className={`w-full px-4 py-3 rounded-xl border ${
                    darkMode
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  required
                />
              </div>

              {/* Content - Show preview if extracted, allow editing */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Content * {uploadedFile && <span className="text-sm text-gray-500">(Extracted from file)</span>}
                </label>
                <textarea
                  value={contentText}
                  onChange={(e) => setContentText(e.target.value)}
                  placeholder="Paste your notes, summary, quiz questions, or study guide here..."
                  rows={10}
                  className={`w-full px-4 py-3 rounded-xl border ${
                    darkMode
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm`}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Subject */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Subject *
                  </label>
                  <select
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${
                      darkMode
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                    required
                  >
                    <option value="">Select subject...</option>
                    {subjects.map(subj => (
                      <option key={subj} value={subj}>{subj}</option>
                    ))}
                  </select>
                </div>

                {/* Difficulty */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Difficulty Level
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border ${
                      darkMode
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tags */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="e.g., chemistry, reactions, organic"
                    className={`w-full px-4 py-3 rounded-xl border ${
                      darkMode
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  />
                </div>

                {/* Estimated Time */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Est. Study Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={estimatedTime}
                    onChange={(e) => setEstimatedTime(e.target.value)}
                    placeholder="e.g., 15"
                    className={`w-full px-4 py-3 rounded-xl border ${
                      darkMode
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  />
                </div>
              </div>

              {/* Share to Discover - only for new content */}
              {!editingContent && (
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-purple-900/20 border-purple-500/50' : 'bg-purple-50 border-purple-200'}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shareToDiscover}
                      onChange={(e) => setShareToDiscover(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <div className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Share to Discover Feed
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                        Share your content with the community. AI will verify quality and accuracy. Earn 50 points if approved!
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {error && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/50 text-red-500 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3">
                {uploadedFile && !editingContent && (
                  <button
                    type="button"
                    onClick={() => {
                      setStep('upload');
                      setUploadedFile(null);
                      setExtractedContent(null);
                      setTitle('');
                      setDescription('');
                      setContentText('');
                    }}
                    className={`px-6 py-4 rounded-xl font-semibold ${
                      darkMode
                        ? 'bg-gray-700 text-white hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                    } transition-colors`}
                  >
                    Upload Different File
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold text-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>{editingContent ? 'Update Content' : (shareToDiscover ? 'Verify & Upload' : 'Save to My Notes')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {step === 'verifying' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                AI is Verifying Your Content
              </h3>
              <p className={`text-center mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Checking accuracy, readability, and educational value...
              </p>
              <div className="space-y-3 w-full max-w-md">
                {['Fact-checking content', 'Analyzing readability', 'Assessing educational value', 'Generating feedback'].map((step, idx) => (
                  <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'results' && verificationResult && (
            <div className="space-y-6">
              {/* Verification Status */}
              <div className={`p-6 rounded-2xl border-2 ${
                verificationResult.isApproved
                  ? darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                  : darkMode ? 'bg-yellow-900/20 border-yellow-500' : 'bg-yellow-50 border-yellow-500'
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    verificationResult.isApproved ? 'bg-green-500' : 'bg-yellow-500'
                  }`}>
                    {verificationResult.isApproved ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-2 ${
                      verificationResult.isApproved
                        ? 'text-green-700 dark:text-green-300'
                        : 'text-yellow-700 dark:text-yellow-300'
                    }`}>
                      {verificationResult.isApproved ? (editingContent ? 'Content Updated!' : 'Content Approved!') : 'Needs Improvement'}
                    </h3>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                      {verificationResult.feedback}
                    </p>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <div>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Quality Score: </span>
                        <span className="font-bold">{(verificationResult.score * 100).toFixed(0)}%</span>
                      </div>
                      <div>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Readability: </span>
                        <span className="font-bold">{(verificationResult.readabilityScore * 100).toFixed(0)}%</span>
                      </div>
                      <div>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Educational Value: </span>
                        <span className="font-bold">{(verificationResult.educationalValue * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strengths */}
              {verificationResult.strengths && verificationResult.strengths.length > 0 && (
                <div>
                  <h4 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ✨ Strengths
                  </h4>
                  <ul className="space-y-2">
                    {verificationResult.strengths.map((strength, idx) => (
                      <li key={idx} className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {verificationResult.improvements && verificationResult.improvements.length > 0 && (
                <div>
                  <h4 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    💡 Suggested Improvements
                  </h4>
                  <ul className="space-y-2">
                    {verificationResult.improvements.map((improvement, idx) => (
                      <li key={idx} className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <span>{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Points Earned - only for new approved content */}
              {verificationResult.isApproved && shareToDiscover && !editingContent && (
                <div className={`p-6 rounded-2xl text-center ${
                  darkMode ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30' : 'bg-gradient-to-r from-purple-100 to-pink-100'
                }`}>
                  <Award className="w-16 h-16 text-yellow-500 mx-auto mb-3" />
                  <h4 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    You earned 50 points!
                  </h4>
                  <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Your content has been published to the Discover feed
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                {!verificationResult.isApproved && (
                  <button
                    onClick={() => setStep('form')}
                    className={`flex-1 py-3 rounded-xl border-2 font-semibold ${
                      darkMode
                        ? 'border-purple-500 text-purple-400 hover:bg-purple-500/10'
                        : 'border-purple-500 text-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    Improve & Resubmit
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:scale-105 transition-transform"
                >
                  {verificationResult.isApproved ? 'Done' : 'Save Draft'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadContentModal;
